/**
 * Copyright (c) 2023 Gitpod GmbH. All rights reserved.
 * Licensed under the GNU Affero General Public License (AGPL).
 * See License.AGPL.txt in the project root for license information.
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/components/podkit/lib/cn";

const ButtonVariants = cva(
    "inline-flex items-center gap-1.5 justify-center whitespace-nowrap rounded-lg text-base font-medium transition-colors disabled:pointer-events-none disabled:text-content-tertiary",
    {
        variants: {
            variant: {
                default:
                    "bg-transparent hover:bg-surface-tertiary border border-border-light text-content-primary disabled:bg-surface-tertiary",
                destructive: "bg-red-600 hover:bg-red-700 text-gray-100 dark:text-red-100 disabled:bg-surface-tertiary",
                outline: "border border-border-base bg-transparent text-content-primary",
                secondary:
                    "bg-transparent hover:bg-surface-tertiary border-border-base border focus-visible:ring-0 focus-visible:border-[color:var(--tw-ring-color)]",
                secondaryNonTransparent:
                    "bg-surface-secondary hover:bg-surface-primary border-border-base border focus-visible:ring-0 focus-visible:border-[color:var(--tw-ring-color)]",
                ghost: "bg-transparent hover:bg-opacity-50",
                link: "text-gray-500 dark:text-gray-100 underline-offset-4 hover:underline",
                brand: "bg-kumquat-800 text-always-light hover:bg-kumquat-900 focus-visible:bg-kumquat-900 disabled:bg-surface-tertiary",
                invert: "bg-surface-invert text-content-invert hover:bg-content-tertiary disabled:bg-surface-tertiary",
            },
            size: {
                default: "h-9 rounded-xl px-4 py-2",
                sm: "h-7 rounded-lg px-2 text-xs",
                lg: "h-11 rounded-lg px-8",
                icon: "h-9 w-9 p-0 rounded-sm",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export type ButtonVariant = VariantProps<typeof ButtonVariants>["variant"];

export type ButtonProps = {
    asChild?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
    VariantProps<typeof ButtonVariants>;

/**
 * TODO: import `Button` from `@/components/flexkit/Button` this file and be happy! 🍻
 *
 * @deprecated
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        return <Comp className={cn(ButtonVariants({ variant, size, className }))} ref={ref} {...props} />;
    },
);
Button.displayName = "Button";
